import React, { useState, useEffect } from 'react';
import { UserPlus, UserCheck, Loader, Users } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import followService from '../../services/follow.service';
import { toast } from 'react-hot-toast';

const FollowButton = ({ 
  userId, 
  size = 'md', 
  showCount = false, 
  onFollowChange,
  className = '' 
}) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const { user: currentUser } = useAuthStore();

  useEffect(() => {
    if (currentUser && userId) {
      loadFollowStatus();
    }
  }, [currentUser, userId]);

  const loadFollowStatus = async () => {
    if (currentUser.id === userId) return;
    
    try {
      const status = await followService.checkFollowStatus('current', userId);
      setIsFollowing(status.following);
    } catch (error) {
      console.error('Failed to load follow status:', error);
    }
  };

  const handleFollowToggle = async () => {
    if (!currentUser) {
      toast.error('Please login to follow users');
      return;
    }

    if (currentUser.id === userId) {
      toast.error('You cannot follow yourself');
      return;
    }

    setIsLoading(true);
    try {
      const result = await followService.toggleFollow(userId);
      setIsFollowing(result.following);
      
      if (result.stats) {
        setFollowersCount(result.stats.followersCount || 0);
      } else {
        setFollowersCount(prev => result.following ? prev + 1 : Math.max(0, prev - 1));
      }
      
      if (onFollowChange) {
        onFollowChange(result.following);
      }
      
      toast.success(result.following ? 'Followed successfully!' : 'Unfollowed successfully!');
    } catch (error) {
      console.error('Follow toggle error:', error);
      toast.error(error.message || 'Failed to follow user');
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentUser || currentUser.id === userId) {
    return null;
  }

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  const iconSize = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  return (
    <button
      onClick={handleFollowToggle}
      disabled={isLoading}
      className={`
        group flex items-center justify-center gap-2 font-medium rounded-lg transition-all
        ${isFollowing
          ? 'bg-white text-gray-700 shadow-neomorph hover:shadow-neomorph-hover border border-gray-200'
          : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md shadow-primary-500/30 hover:shadow-lg'
        }
        ${sizeClasses[size]}
        ${className}
        disabled:opacity-50 disabled:cursor-not-allowed
      `}
    >
      {isLoading ? (
        <Loader className={`${iconSize[size]} animate-spin ${isFollowing ? 'text-gray-500' : 'text-white'}`} />
      ) : isFollowing ? (
        <>
          <div className="p-0.5 bg-gray-100 rounded group-hover:bg-gray-200 transition-all">
            <UserCheck className={iconSize[size] + ' text-gray-600'} />
          </div>
          <span>Following</span>
        </>
      ) : (
        <>
          <div className="p-0.5 bg-white/20 rounded group-hover:bg-white/30 transition-all">
            <UserPlus className={iconSize[size]} />
          </div>
          <span>Follow</span>
        </>
      )}
      
      {showCount && followersCount > 0 && (
        <span className={`
          inline-flex items-center ml-1 px-2 py-0.5 text-xs rounded-lg
          ${isFollowing 
            ? 'bg-gray-100 text-gray-700 border border-gray-200' 
            : 'bg-white/20 text-white border border-white/30'
          }
        `}>
          <Users className="h-3 w-3 mr-1" />
          {followersCount}
        </span>
      )}
    </button>
  );
};

export default FollowButton;